/**
 * Loop Widget - One-time Purchase Discount Badge + Pack Size Price Sync
 * - Adds discount badge to one-time purchase option from product metafield
 * - Updates Loop widget prices (subscription + one-time) when pack size changes
 */

(function() {
    'use strict';
  
    const SELECTORS = {
      onetimePurchaseOption: '[data-loop-widget-onetime-purchase-option]',
      discountBadge: '.loop-widget-purchase-option-discount-badge',
      labelWrapper: '.loop-widget-spg-label-discount-wrapper',
      packSizePicker: '.pack-size-picker',
      packSizeInput: '.pack-size-input',
      packSizeSelectSticky: '.pack-size-select-sticky',
      loopPrice: '.loop-widget-purchase-option-price',
      loopCompareAtPrice: '.loop-widget-purchase-option-compare-at-price',
      loopContainer: '.loop-widget-container'
    };
  
    const MAX_ATTEMPTS = 50;
    const RETRY_DELAY = 100;
    const UNIT_PRICE_ATTR = 'data-loop-unit-price-cents';
    const LAST_QTY_ATTR = 'data-loop-last-qty';
    const COMPARE_UNIT_ATTR = 'data-loop-compare-unit-cents';
    const COMPARE_LAST_QTY_ATTR = 'data-loop-compare-last-qty';
  
    /**
     * Wait for the Loop widget to be rendered in the DOM
     */
    function waitForLoopWidget(callback, attempts = 0) {
      const onetimeOption = document.querySelector(SELECTORS.onetimePurchaseOption);
  
      if (onetimeOption) {
        callback(onetimeOption);
      } else if (attempts < MAX_ATTEMPTS) {
        setTimeout(() => waitForLoopWidget(callback, attempts + 1), RETRY_DELAY);
      }
    }
  
    /**
     * Add discount badge to one-time purchase option
     */
    function addDiscountBadge(onetimeOption) {
      const labelWrapper = onetimeOption.querySelector(SELECTORS.labelWrapper);
      const existingBadge = onetimeOption.querySelector(SELECTORS.discountBadge);
  
      if (!labelWrapper) return;
  
      const productInfo = document.querySelector('[data-product-id]');
      const discountBadgeText = productInfo?.dataset?.discountBadge;
  
      if (!discountBadgeText) return;
  
      if (existingBadge) {
        existingBadge.textContent = discountBadgeText;
        existingBadge.classList.remove('loop-display-none');
      } else {
        const badge = document.createElement('div');
        badge.className = 'loop-widget-purchase-option-discount-badge';
        badge.textContent = discountBadgeText;
        labelWrapper.appendChild(badge);
      }
    }
  
    /**
     * Parse numeric price from displayed string (e.g. "$17.00" -> 17.00)
     */
    function parsePrice(text) {
      if (!text || typeof text !== 'string') return 0;
      const cleaned = text.replace(/[^0-9.]/g, '');
      return parseFloat(cleaned) || 0;
    }
  
    /**
     * Format cents as money string using theme formatter or fallback
     */
    function formatPriceCents(cents) {
      if (typeof theme !== 'undefined' && theme.Currency && theme.Currency.formatMoney && theme.shopSettings && theme.shopSettings.moneyFormat) {
        return theme.Currency.formatMoney(Math.round(cents), theme.shopSettings.moneyFormat);
      }
      return '$' + (Math.round(cents) / 100).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    }
  
    /**
     * Get selected pack quantity (1 if no pack size picker)
     */
    function getPackQuantity() {
      const picker = document.querySelector(SELECTORS.packSizePicker);
      if (!picker) return 1;
  
      const checked = picker.querySelector(SELECTORS.packSizeInput + ':checked') || picker.querySelector(SELECTORS.packSizeInput);
      const qty = parseInt(checked?.getAttribute('data-pack-quantity'), 10);
      return (qty > 0 ? qty : 1);
    }
  
  var isUpdatingPrice = false;
  var priceObserver = null;
  var priceDebounceTimer = null;

  /**
   * Setup MutationObserver on price elements - when Loop updates them, re-apply our multiplier
   */
  function setupPriceObserver() {
    var container = document.querySelector(SELECTORS.loopContainer);
    if (!container) return;

    if (priceObserver) priceObserver.disconnect();

    priceObserver = new MutationObserver(function() {
      if (isUpdatingPrice) return;
      clearTimeout(priceDebounceTimer);
      priceDebounceTimer = setTimeout(function() {
        syncLoopPricesWithPackSize();
      }, 50);
    });

    container.querySelectorAll(SELECTORS.loopPrice).forEach(function(el) {
      priceObserver.observe(el, { characterData: true, subtree: true, childList: true });
    });
  }

  /**
   * Clear unit price cache (e.g. on variant change when Loop updates prices)
   */
  function clearUnitPriceCache() {
      const container = document.querySelector(SELECTORS.loopContainer);
      if (!container) return;
      container.querySelectorAll(SELECTORS.loopPrice).forEach(function(el) {
        el.removeAttribute(UNIT_PRICE_ATTR);
        el.removeAttribute(LAST_QTY_ATTR);
      });
      container.querySelectorAll(SELECTORS.loopCompareAtPrice).forEach(function(el) {
        el.removeAttribute(COMPARE_UNIT_ATTR);
        el.removeAttribute(COMPARE_LAST_QTY_ATTR);
      });
    }
  
  /**
   * Sync Loop widget prices with pack size (price x quantity = total)
   * Overwrites in place; MutationObserver triggers when Loop updates prices.
   */
  function syncLoopPricesWithPackSize() {
    var picker = document.querySelector(SELECTORS.packSizePicker);
    var quantity = getPackQuantity();
    var container = document.querySelector(SELECTORS.loopContainer);
    if (!container) return;

    isUpdatingPrice = true;
    var priceEls = container.querySelectorAll(SELECTORS.loopPrice);
    priceEls.forEach(function(priceEl) {
      var displayed = parsePrice(priceEl.textContent);
      if (!displayed) return;

      var unitCents = parseInt(priceEl.getAttribute(UNIT_PRICE_ATTR), 10);
      var lastQty = parseInt(priceEl.getAttribute(LAST_QTY_ATTR), 10);
      var displayedCents = Math.round(displayed * 100);
      var expectedFromUs = unitCents && lastQty ? unitCents * lastQty : 0;

      if (!unitCents || !lastQty || Math.abs(displayedCents - expectedFromUs) > 1) {
        unitCents = displayedCents;
        priceEl.setAttribute(UNIT_PRICE_ATTR, String(unitCents));
      }

      var totalCents = unitCents * quantity;
      var newText = formatPriceCents(totalCents);
      if (priceEl.textContent !== newText) priceEl.textContent = newText;
      priceEl.setAttribute(LAST_QTY_ATTR, String(quantity));
    });
    isUpdatingPrice = false;

    if (!picker) return;

    var compareEls = container.querySelectorAll(SELECTORS.loopCompareAtPrice);
      compareEls.forEach(function(compareEl) {
        const displayed = parsePrice(compareEl.textContent);
        if (!displayed) return;
  
        let unitCents = parseInt(compareEl.getAttribute(COMPARE_UNIT_ATTR), 10);
        const lastQty = parseInt(compareEl.getAttribute(COMPARE_LAST_QTY_ATTR), 10);
        const displayedCents = Math.round(displayed * 100);
        const expectedFromUs = unitCents && lastQty ? unitCents * lastQty : 0;
  
        if (!unitCents || !lastQty || Math.abs(displayedCents - expectedFromUs) > 1) {
          unitCents = displayedCents;
          compareEl.setAttribute(COMPARE_UNIT_ATTR, String(unitCents));
        }
  
        const totalCents = unitCents * quantity;
        compareEl.textContent = formatPriceCents(totalCents);
        compareEl.setAttribute(COMPARE_LAST_QTY_ATTR, String(quantity));
      });
    }
  
    /**
     * Handle variant change: re-apply discount badge, clear price cache, sync prices
     */
  function onVariantChange() {
    clearUnitPriceCache();
    setTimeout(function() {
      var onetime = document.querySelector(SELECTORS.onetimePurchaseOption);
      if (onetime) addDiscountBadge(onetime);
      setupPriceObserver();
      syncLoopPricesWithPackSize();
    }, 300);
  }
  
    /**
     * Setup pack size price sync: listener + variant-change
     */
    function setupPackSizePriceSync() {
      const picker = document.querySelector(SELECTORS.packSizePicker);
      if (picker) {
        document.addEventListener('change', function(e) {
          var t = e.target;
          if (!t || !t.matches) return;
          if (t.matches(SELECTORS.packSizeInput)) {
            syncLoopPricesWithPackSize();
          } else if (t.matches(SELECTORS.packSizeSelectSticky)) {
            setTimeout(syncLoopPricesWithPackSize, 0);
          }
        });
      }
  
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.variantChange) {
        subscribe(PUB_SUB_EVENTS.variantChange, onVariantChange);
      }
    }
  
    /**
     * Initialize when Loop widget is ready
     */
  function onLoopWidgetReady(onetimeOption) {
    addDiscountBadge(onetimeOption);
    setupPriceObserver();
    setupPackSizePriceSync();
    syncLoopPricesWithPackSize();
  }
  
    /**
     * Initialize when DOM is ready
     */
    function init() {
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', function() {
          waitForLoopWidget(onLoopWidgetReady);
        });
      } else {
        waitForLoopWidget(onLoopWidgetReady);
      }
    }
  
    init();
  })();
  